import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { CircleNotch, FilePdf, Image, Printer } from '@phosphor-icons/react';
import {
  STANDARD_QR_LABEL_SIZES,
  getStandardDimensions,
  DEFAULT_STANDARD_SIZE_ID,
  formatSizeLabel,
  unitToMm,
} from '../lib/labelSizes';
import {
  downloadLabelsAsPdf,
  downloadLabelsAsPng,
  labelCaption,
  openLabelPrintSheet,
} from '../lib/printSheet';
import { printLabelsDirect } from '../lib/labelPrinter';
import { toast } from 'sonner';

function matchSizeId(width, height, unit = 'mm') {
  const wMm = Math.round(unitToMm(Number(width), unit) * 100) / 100;
  const hMm = Math.round(unitToMm(Number(height), unit) * 100) / 100;
  const match = STANDARD_QR_LABEL_SIZES.find(
    (s) => s.widthMm === wMm && s.heightMm === hMm,
  );
  return match?.id || null;
}

/**
 * Preview labels before exporting to printer, PNG, or PDF.
 */
export default function PrintPreviewDialog({
  open,
  onOpenChange,
  labels = [],
  title = 'Print preview',
  defaultSizeId = DEFAULT_STANDARD_SIZE_ID,
  /** Optional sheet size from a print job */
  sheetWidth,
  sheetHeight,
  sheetUnit = 'mm',
  printerProfile = null,
  onPrinted,
}) {
  const [sizeId, setSizeId] = useState(defaultSizeId);
  const [useSheetSize, setUseSheetSize] = useState(false);
  const [busy, setBusy] = useState(null); // 'print' | 'png' | 'pdf' | null

  useEffect(() => {
    if (!open) return;
    const hasSheet = sheetWidth != null && sheetHeight != null;
    setUseSheetSize(hasSheet);
    if (hasSheet) {
      setSizeId(matchSizeId(sheetWidth, sheetHeight, sheetUnit) || defaultSizeId);
    } else {
      setSizeId(defaultSizeId);
    }
  }, [open, sheetWidth, sheetHeight, sheetUnit, defaultSizeId]);

  const dims = useMemo(() => {
    if (useSheetSize && sheetWidth != null && sheetHeight != null) {
      return {
        width: Number(sheetWidth),
        height: Number(sheetHeight),
        unit: sheetUnit || 'mm',
      };
    }
    const d = getStandardDimensions(sizeId, 'mm');
    return { width: d.width, height: d.height, unit: 'mm' };
  }, [useSheetSize, sizeId, sheetWidth, sheetHeight, sheetUnit]);

  const handleSizeChange = (id) => {
    setUseSheetSize(false);
    setSizeId(id);
  };

  const previewLabels = labels.filter((l) => l?.qr_image);

  const close = () => onOpenChange?.(false);

  const run = async (mode) => {
    if (!previewLabels.length) {
      toast.error('No QR images available to export');
      return;
    }
    setBusy(mode);
    try {
      if (mode === 'print') {
        const profile = printerProfile
          ? {
              ...printerProfile,
              width_mm:
                dims.unit === 'cm' ? dims.width * 10 : dims.width,
              height_mm:
                dims.unit === 'cm' ? dims.height * 10 : dims.height,
            }
          : null;
        await printLabelsDirect({
          labels: previewLabels,
          printerProfile: profile,
          fallbackPrint: () =>
            openLabelPrintSheet({
              labels: previewLabels,
              width: dims.width,
              height: dims.height,
              unit: dims.unit,
              title: `${title} (${previewLabels.length})`,
            }),
        });
        toast.success(`Sent ${previewLabels.length} label(s) to printer`);
        onPrinted?.();
      } else if (mode === 'png') {
        const n = await downloadLabelsAsPng(previewLabels, { basename: 'omnichek-qr' });
        toast.success(`Downloaded ${n} PNG file(s)`);
      } else if (mode === 'pdf') {
        const pdfW = dims.unit === 'cm' ? dims.width * 10 : dims.width;
        const pdfH = dims.unit === 'cm' ? dims.height * 10 : dims.height;
        await downloadLabelsAsPdf(previewLabels, {
          width: pdfW,
          height: pdfH,
          unit: 'mm',
          filename: `omnichek-qr-labels-${previewLabels.length}.pdf`,
        });
        toast.success(`Downloaded PDF (${previewLabels.length} page(s))`);
      }
    } catch (err) {
      toast.error(err?.message || 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:rounded-modal">
        <DialogHeader className="border-b border-border px-6 py-5 text-left">
          <DialogTitle className="font-heading text-xl">Print preview</DialogTitle>
          <DialogDescription>
            Review {previewLabels.length} label{previewLabels.length === 1 ? '' : 's'}, then print to a
            printer or download as PNG / PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto px-6 py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="w-full max-w-xs space-y-2">
              <Label>Label size</Label>
              <Select value={sizeId} onValueChange={handleSizeChange}>
                <SelectTrigger data-testid="print-preview-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STANDARD_QR_LABEL_SIZES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {formatSizeLabel(s.widthMm, s.heightMm, 'mm')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              {dims.width} × {dims.height} {dims.unit} · {previewLabels.length} ready
            </p>
          </div>

          {!previewLabels.length ? (
            <div className="rounded-card border border-dashed border-border bg-muted/40 px-6 py-16 text-center text-sm text-muted-foreground">
              No label images to preview.
            </div>
          ) : (
            <div className="grid max-h-[46vh] grid-cols-2 gap-3 overflow-y-auto rounded-card border border-border bg-muted/30 p-4 sm:grid-cols-3 md:grid-cols-4">
              {previewLabels.map((label, idx) => (
                <div
                  key={label.qr_id || idx}
                  className="flex flex-col items-center rounded-xl border border-border bg-card p-3 shadow-oc-sm"
                >
                  <img
                    src={`data:image/png;base64,${label.qr_image}`}
                    alt={labelCaption(label)}
                    className="h-28 w-28 object-contain"
                  />
                  <p className="mt-2 w-full truncate text-center text-[11px] text-muted-foreground">
                    {labelCaption(label)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 border-t border-border bg-muted/20 px-6 py-4 sm:flex-row sm:justify-between">
          <Button variant="outline" onClick={close} disabled={Boolean(busy)}>
            Cancel
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => run('png')}
              disabled={Boolean(busy) || !previewLabels.length}
              data-testid="print-download-png"
            >
              {busy === 'png' ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}
              Download PNG
            </Button>
            <Button
              variant="outline"
              onClick={() => run('pdf')}
              disabled={Boolean(busy) || !previewLabels.length}
              data-testid="print-download-pdf"
            >
              {busy === 'pdf' ? <CircleNotch className="h-4 w-4 animate-spin" /> : <FilePdf className="h-4 w-4" />}
              Download PDF
            </Button>
            <Button
              onClick={() => run('print')}
              disabled={Boolean(busy) || !previewLabels.length}
              data-testid="print-to-printer"
            >
              {busy === 'print' ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Print to printer
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
